import * as Sentry from "@sentry/react";
import type { RootOptions } from "react-dom/client";

/** Sentry settings of this deployment, injected by the server into index.html. */
export interface SentryConfig {
  dsn: string;
  environment: string;
}

export function readSentryConfig(doc: Document = document): SentryConfig | null {
  const content = doc.querySelector<HTMLMetaElement>('meta[name="suivi-sentry"]')?.content;
  if (!content) return null;
  try {
    const config = JSON.parse(content) as Partial<SentryConfig>;
    return config.dsn ? { dsn: config.dsn, environment: config.environment ?? "production" } : null;
  } catch {
    return null;
  }
}

/**
 * Starts error reporting when the server configured it (no meta tag in development or
 * tests). Returns the createRoot options that report render errors, React's defaults otherwise.
 */
export function initSentry(): RootOptions {
  const config = readSentryConfig();
  if (!config) return {};
  // Errors only. No user data, cookies or bodies: they carry e-mail addresses.
  Sentry.init({ ...config, dataCollection: { userInfo: false, cookies: false, httpBodies: [] } });
  return {
    onUncaughtError: Sentry.reactErrorHandler((error) => console.error(error)),
    // Render errors shown by the router's error page (RouteError), which logs them itself.
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  };
}
